import { useLazyQuery, useMutation } from '@apollo/client'
import { GraphQLError } from 'graphql'
import { ComponentPropsWithoutRef, createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { NETWORK_COPY_FROM_DEFAULT_MUTATION } from 'src/graphql/mutation/networkCopyFromDefault'

import { NETWORK_PUBLISH_MUTATION } from 'src/graphql/mutation/networkPublish'
import { SERVICE_BRAND_GET } from 'src/graphql/service/serviceGet'
import fetchHelper from 'src/helpers/fetchHelper'
import {
  BrandNetworkContextType,
  GetNetworkDataFetchCategoryType,
  InitialStateType,
  NetworkBrandType,
  NetworkBuildInfoType,
  NetworkCopyPayloadType,
  NetworkSettingsType,
  NetworkType,
} from 'src/types/AppCms/Brand'

const InitialState: InitialStateType = {
  loadingNetwork: false,
  networks: null,
  currentNetwork: null,
  defaultNetwork: null,
  message: undefined,
  syncSuccess: false,
}

const BrandNetworkContext = createContext<BrandNetworkContextType>({
  ...InitialState,
  onChangeNetwork: () => {},
  getSettingsForSelectedNetwork: async () => ({}),
  getBrandForSelectedNetwork: async () => ({}),
  getBuildInfoForSelectedNetwork: async () => ({}),
  launchNetwork: async () => {},
  networkCopyFromDefault: async () => {},
  setSyncSuccess: () => {},
})

export function useBrandNetworkContext() {
  return useContext(BrandNetworkContext)
}

export default memo(function BrandNetworkProvider({ children }: ComponentPropsWithoutRef<'main'>) {
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [cookies] = useCookies()
  const [serviceGetHandler] = useLazyQuery(SERVICE_BRAND_GET)
  const [networkPublishHandler] = useMutation(NETWORK_PUBLISH_MUTATION)
  const [networkCopyHandler] = useMutation(NETWORK_COPY_FROM_DEFAULT_MUTATION)
  const [syncSuccess, setSyncSuccess] = useState(false)

  const [networks, setNetworks] = useState<NetworkType[]>([])
  const [currentNetwork, setCurrentNetwork] = useState<NetworkType | null>(null)
  const [loadingNetwork, setLoadingNetwork] = useState(false)
  const [message, setMessage] = useState<{ text: string; type?: 'error' | 'success' } | undefined>(undefined)

  const defaultNetwork = networks.find((network) => network.isDefault) || null

  const Authorization = cookies['vl-accessToken']
  const xApiKey = cookies.managementXApiKey
  const site = cookies.site

  const getNetworkList = useCallback(async () => {
    if (!site || !xApiKey || !Authorization) return
    setLoadingNetwork(true)
    try {
      const res = await serviceGetHandler({
        variables: { site },
        context: { headers: { xApiKey, Authorization } },
      })
      if (res.error) throw res.error
      const _networks = res.data?.serviceGet?.service?.networks
      if (!_networks || !_networks.length) return setLoadingNetwork(false)
      const sessionNetwork = sessionStorage.getItem('currentNetwork') // identifier
      const currentDefault = sessionNetwork
        ? _networks.find((network: NetworkType) => network.identifier === sessionNetwork)
        : _networks.find((network: NetworkType) => network.isDefault)
      if (currentDefault) setCurrentNetwork(currentDefault)
      setNetworks(_networks)
      setLoadingNetwork(false)
      return
    } catch (error) {
      setLoadingNetwork(false)
      if (error instanceof GraphQLError) {
        setMessage({ text: error.message, type: 'error' })
      } else {
        setMessage({ text: (error as Error).message || 'Failed to fetch networks', type: 'error' })
      }
    }
  }, [serviceGetHandler, site, xApiKey, Authorization])

  async function onChangeNetwork(identifier: string) {
    const network = networks?.find((network: NetworkType) => network.identifier === identifier)
    if (network) {
      setCurrentNetwork(network)
      sessionStorage.setItem('currentNetwork', identifier)
      if (!network.isDefault && !site) return setMessage({ text: 'Site not found', type: 'error' })
    } else {
      setCurrentNetwork(null)
      setMessage({ text: `Network with identifier ${identifier} not found`, type: 'error' })
    }
  }

  async function getDataForNetwork<FetchType>(
    selectedNetwork: NetworkType,
    fetchCategory: GetNetworkDataFetchCategoryType
  ): Promise<FetchType | null> {
    if (!xApiKey || !Authorization || !site || selectedNetwork.isDefault) return null
    const baseUrl = `${process.env.NEXT_PUBLIC_V4_API_URL}/appcms/${site}/${fetchCategory}/${selectedNetwork.identifier}`
    try {
      setLoadingNetwork(true)
      const res = await fetchHelper({
        method: 'GET',
        url: baseUrl,
        headers: { xapikey: xApiKey, Authorization },
      })
      if (res instanceof Error) throw res
      setLoadingNetwork(false)
      if ('error' in res) throw new Error(res.error)
      if ('status' in res && typeof res.status === 'number') delete res.status
      return res as FetchType
    } catch (err) {
      setLoadingNetwork(false)
      setMessage({
        text: `${selectedNetwork.name} | ${(err as Error)?.message}` || `Failed to get ${fetchCategory} for ${selectedNetwork.name}`,
        type: 'error',
      })
      return null
    }
  }

  async function getSettingsForSelectedNetwork(selectedNetwork: NetworkType): Promise<NetworkSettingsType | null> {
    return await getDataForNetwork(selectedNetwork, 'settings')
  }

  async function getBrandForSelectedNetwork(selectedNetwork: NetworkType): Promise<NetworkBrandType | null> {
    return await getDataForNetwork(selectedNetwork, 'brand')
  }

  async function getBuildInfoForSelectedNetwork(selectedNetwork: NetworkType): Promise<NetworkBuildInfoType | null> {
    return await getDataForNetwork(selectedNetwork, 'device')
  }

  async function launchNetwork() {
    try {
      if (!xApiKey || !Authorization) return setMessage({ text: 'Authorization or API key missing', type: 'error' })
      if (!site) return setMessage({ text: 'Site not found', type: 'error' })
      if (!currentNetwork) return setMessage({ text: 'No network selected', type: 'error' })
      if (currentNetwork.isDefault) return setMessage({ text: `${currentNetwork.name} is the default network.`, type: 'error' })
      setLoadingNetwork(true)
      const res = await networkPublishHandler({
        variables: { site, network: currentNetwork.identifier },
        context: { headers: { xApiKey, Authorization } },
      })
      if (res?.errors && res?.errors?.length > 0) throw res.errors[0]
      if (!res?.data?.networkPublish?.success) throw new Error(`Failed to launch ${currentNetwork?.name}`)
      setMessage({ text: `Successfully launched ${currentNetwork?.name}`, type: 'success' })
      setLoadingNetwork(false)
    } catch (err) {
      setLoadingNetwork(false)
      if (err instanceof GraphQLError) return setMessage({ text: err.message, type: 'error' })
      return setMessage({
        text: (err as Error)?.message || `Failed to launch ${currentNetwork?.name}`,
        type: 'error',
      })
    }
  }

  async function networkCopyFromDefault(copyCategory: 'settings' | 'brand') {
    try {
      if (!site) throw new Error('Site not found')
      if (!currentNetwork) throw new Error('No network selected')
      if (currentNetwork.isDefault) throw new Error(`${currentNetwork.name} is the default network.`)
      if (!xApiKey || !Authorization) throw new Error('Authorization or API key missing')

      const variables: NetworkCopyPayloadType = {
        req: {
          site: site || '',
          network: currentNetwork?.identifier || '',
          networkBrand: {
            logoAndIcons: copyCategory === 'brand',
            theme: copyCategory === 'brand',
            themeOtt: copyCategory === 'brand',
            themeApps: copyCategory === 'brand',
          },
          networkSettings: copyCategory === 'settings',
        },
      }

      const res = await networkCopyHandler({
        variables,
        context: { headers: { xApiKey, Authorization } },
      })

      if (res.errors) throw res.errors[0]
      if (res.data?.networkCopyFromDefault?.success)
        setMessage({
          text: `Successfully copied ${copyCategory} from ${defaultNetwork?.name} to ${currentNetwork.name}`,
          type: 'success',
        })
      setLoadingNetwork(false)
      return setSyncSuccess(res.data?.networkCopyFromDefault?.success)
    } catch (err) {
      setLoadingNetwork(false)
      if (err instanceof GraphQLError) return setMessage({ text: err.message, type: 'error' })
      return setMessage({
        text: (err as Error)?.message || `Failed to launch ${currentNetwork?.name}`,
        type: 'error',
      })
    }
  }

  useEffect(() => {
    if (networks === null || networks?.length === 0) {
      getNetworkList()
    }
  }, [getNetworkList, networks])

  useEffect(() => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current)
    }
    if (message?.text) {
      errorTimerRef.current = setTimeout(() => {
        setMessage(undefined)
      }, 3000)
    }
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current)
      }
    }
  }, [message?.text])

  const contextValue = useMemo(
    () => ({
      loadingNetwork,
      networks,
      currentNetwork,
      message,
      defaultNetwork,
      onChangeNetwork,
      getSettingsForSelectedNetwork,
      getBrandForSelectedNetwork,
      getBuildInfoForSelectedNetwork,
      launchNetwork,
      networkCopyFromDefault,
      syncSuccess,
      setSyncSuccess,
    }),
    [
      loadingNetwork,
      networks,
      currentNetwork,
      message,
      defaultNetwork,
      onChangeNetwork,
      getSettingsForSelectedNetwork,
      getBrandForSelectedNetwork,
      getBuildInfoForSelectedNetwork,
      launchNetwork,
      syncSuccess,
      setSyncSuccess,
      networkCopyFromDefault,
    ]
  )

  return <BrandNetworkContext.Provider value={contextValue}>{children}</BrandNetworkContext.Provider>
})
